import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { date, dateTime, inr, num } from '../lib/format';
import { Badge, Button, Card, CardHead, Empty, Field, Input, Modal, Notice, Select, useToast } from '../components/ui';
import type { BadgeTone } from '../components/ui';

interface Pool {
  category: string;
  label: string;
  percent: number;
  poolPaise: number;
  eligibleUserCount: number;
  perUserPaise: number;
  remainderPaise: number;
  eligibilityRule: string;
}

interface Distribution {
  id: string;
  financialYear: string;
  periodStart: string;
  periodEnd: string;
  turnoverPaise: number;
  gstCollectedPaise: number;
  refundedPaise: number;
  netProfitPaise: number;
  status: 'PENDING_CALCULATION' | 'CALCULATED' | 'DISTRIBUTED';
  visibility: 'HIDDEN' | 'REVEALED';
  calculatedAt: string | null;
  distributedAt: string | null;
  revealedAt: string | null;
  notes: string | null;
  pools: Pool[];
}

interface Rule {
  key: string;
  label: string;
  describe: string;
}

interface ListResponse {
  items: Distribution[];
  suggestedYear: string;
  rules: Rule[];
}

interface Category {
  key: string;
  label: string;
  percent: number;
  rule: string;
}

const STATUS: Record<Distribution['status'], { label: string; tone: BadgeTone }> = {
  PENDING_CALCULATION: { label: 'Pending calculation', tone: 'neutral' },
  CALCULATED: { label: 'Calculated', tone: 'blue' },
  DISTRIBUTED: { label: 'Distributed', tone: 'green' },
};

/**
 * v2 §28 profit distribution.
 *
 * Deliberately sequential — open the year, enter the audited profit, calculate,
 * distribute, reveal — because each step is a decision someone should have to
 * make on purpose, and the audit log records who made it.
 */
export function ProfitDistributionPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const [calculating, setCalculating] = useState<Distribution | null>(null);
  const [revealing, setRevealing] = useState<Distribution | null>(null);
  const [editingConfig, setEditingConfig] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['profit'],
    queryFn: () => api.get<ListResponse>('/admin/profit'),
  });
  const config = useQuery({ queryKey: ['profit-config'], queryFn: () => api.get<Category[]>('/admin/profit/config') });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['profit'] });
    qc.invalidateQueries({ queryKey: ['profit-config'] });
  };

  const openYear = useMutation({
    mutationFn: (financialYear: string) => api.post('/admin/profit/years', { financialYear }),
    onSuccess: () => {
      toast('Financial year opened', 'success');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const distribute = useMutation({
    mutationFn: (id: string) => api.post(`/admin/profit/${id}/distribute`, {}),
    onSuccess: () => {
      toast('Allocations written — still hidden from members', 'success');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const setVisibility = useMutation({
    mutationFn: ({ id, reveal }: { id: string; reveal: boolean }) =>
      api.post(`/admin/profit/${id}/${reveal ? 'reveal' : 'hide'}`, {}),
    onSuccess: (_d, v) => {
      toast(v.reveal ? 'Revealed to eligible members' : 'Hidden again', 'success');
      setRevealing(null);
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const totalPercent = (config.data ?? []).reduce((s, c) => s + c.percent, 0);

  // What the reveal actually exposes. A pool with nobody eligible allocated
  // nothing, so it must not be counted into either figure.
  const revealedPools = (revealing?.pools ?? []).filter((p) => p.perUserPaise > 0);
  const revealedAccounts = revealedPools.reduce((sum, p) => sum + p.eligibleUserCount, 0);
  const revealedTotal = revealedPools.reduce((sum, p) => sum + p.perUserPaise * p.eligibleUserCount, 0);

  return (
    <div className="stack">
      <Notice tone="warn" title="The scheme this serves is under legal review">
        Distributing a share of company profit to members is specified in v2 §28 and is assessed in §0.2(a) as very
        likely a Collective Investment Scheme under the SEBI regulations. What is built here is the calculation and its
        audit trail. <strong>No money leaves the company</strong> — an allocation is a recorded entitlement, and actual
        disbursement is gated exactly as agent bonuses are.
      </Notice>

      <Card flush>
        <CardHead
          title="Categories and eligibility"
          sub="Who is in each category, and what share of net profit it takes"
          action={
            <Button size="sm" onClick={() => setEditingConfig(true)}>
              Configure
            </Button>
          }
        />
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Category</th>
                <th style={{ textAlign: 'right' }}>Share</th>
                <th>Eligible members</th>
              </tr>
            </thead>
            <tbody>
              {(config.data ?? []).map((c) => (
                <tr key={c.key}>
                  <td className="cell-strong">{c.label}</td>
                  <td className="cell-num cell-strong">{c.percent}%</td>
                  <td>{data?.rules.find((r) => r.key === c.rule)?.label ?? c.rule}</td>
                </tr>
              ))}
              <tr>
                <td className="cell-muted">Total allocated</td>
                <td className="cell-num cell-strong">{totalPercent}%</td>
                <td className="cell-muted">{100 - totalPercent}% retained by the company</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card flush>
        <CardHead
          title="Financial years"
          sub="A year can only be opened once it has finished"
          action={
            data?.suggestedYear && !data.items.some((i) => i.financialYear === data.suggestedYear) ? (
              <Button
                size="sm"
                variant="primary"
                loading={openYear.isPending}
                onClick={() => openYear.mutate(data.suggestedYear)}
              >
                + Open {data.suggestedYear}
              </Button>
            ) : undefined
          }
        />
        {isLoading ? null : data?.items.length ? (
          <div className="stack" style={{ padding: 'var(--s5)', gap: 'var(--s5)' }}>
            {data.items.map((d) => (
              <YearCard
                key={d.id}
                d={d}
                onCalculate={() => setCalculating(d)}
                onDistribute={() => distribute.mutate(d.id)}
                onReveal={() => setRevealing(d)}
                onHide={() => setVisibility.mutate({ id: d.id, reveal: false })}
                busy={distribute.isPending || setVisibility.isPending}
              />
            ))}
          </div>
        ) : (
          <Empty
            icon="◔"
            title="No financial year opened yet"
            body="Open the most recently completed year to pull its turnover from captured payments."
          />
        )}
      </Card>

      {calculating && (
        <CalculateDialog
          d={calculating}
          onClose={() => setCalculating(null)}
          onDone={() => {
            setCalculating(null);
            toast('Calculated', 'success');
            invalidate();
          }}
        />
      )}

      {revealing && (
        <Modal
          title={`Reveal ${revealing.financialYear} to members?`}
          sub="This is the only action here that changes what members can see."
          onClose={() => setRevealing(null)}
          footer={
            <>
              <Button onClick={() => setRevealing(null)}>Cancel</Button>
              <Button
                variant="primary"
                loading={setVisibility.isPending}
                onClick={() => setVisibility.mutate({ id: revealing.id, reveal: true })}
              >
                Reveal
              </Button>
            </>
          }
        >
          <div className="stack">
            {/* Allocated, not pooled. A category with nobody eligible keeps
                its whole pool, so quoting the pool total here would promise
                members money that was never assigned to anyone. */}
            <p style={{ margin: 0, lineHeight: 1.65, fontSize: 13.6 }}>
              Every member allocated a share of <strong>{revealing.financialYear}</strong> will be able to see their
              amount in the app from the moment you confirm. That is{' '}
              <strong>
                {num(revealedAccounts)} {revealedAccounts === 1 ? 'account' : 'accounts'}
              </strong>{' '}
              and <strong>{inr(revealedTotal)}</strong> in total.
            </p>
            <Notice tone="info" title="You can hide it again">
              Hiding removes it from the app. It does not delete the allocations, and both actions are recorded in the
              audit log against your account.
            </Notice>
          </div>
        </Modal>
      )}

      {editingConfig && (
        <ConfigDialog
          current={config.data ?? []}
          rules={data?.rules ?? []}
          onClose={() => setEditingConfig(false)}
          onDone={() => {
            setEditingConfig(false);
            toast('Categories saved', 'success');
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function YearCard({
  d,
  onCalculate,
  onDistribute,
  onReveal,
  onHide,
  busy,
}: {
  d: Distribution;
  onCalculate: () => void;
  onDistribute: () => void;
  onReveal: () => void;
  onHide: () => void;
  busy: boolean;
}) {
  const allocated = d.pools.reduce((s, p) => s + p.poolPaise, 0);

  return (
    <Card>
      <div className="row row--wrap" style={{ justifyContent: 'space-between', gap: 'var(--s4)' }}>
        <div>
          <div className="cell-strong" style={{ fontSize: 15 }}>
            {d.financialYear}
          </div>
          <div className="cell-muted">
            {/* periodEnd is exclusive — 1 Apr of the next year — so the last
                day people would name is the one before it. */}
            {date(d.periodStart)} – {date(new Date(new Date(d.periodEnd).getTime() - 86400000))}
          </div>
        </div>
        <div className="row" style={{ gap: 7 }}>
          <Badge tone={STATUS[d.status].tone} dot>
            {STATUS[d.status].label}
          </Badge>
          <Badge tone={d.visibility === 'REVEALED' ? 'orange' : 'neutral'} dot>
            {d.visibility === 'REVEALED' ? 'Revealed' : 'Hidden'}
          </Badge>
        </div>
      </div>

      <div className="row row--wrap" style={{ gap: 'var(--s5)', marginTop: 12 }}>
        <Stat label="Turnover (ex-GST)" value={inr(d.turnoverPaise)} />
        <Stat label="GST collected" value={inr(d.gstCollectedPaise)} />
        <Stat label="Refunded" value={inr(d.refundedPaise)} />
        <Stat label="Net profit (entered)" value={inr(d.netProfitPaise)} />
        <Stat label="Allocated" value={inr(allocated)} />
      </div>

      {d.pools.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="data">
            <thead>
              <tr>
                <th>Category</th>
                <th style={{ textAlign: 'right' }}>Share</th>
                <th style={{ textAlign: 'right' }}>Pool</th>
                <th style={{ textAlign: 'right' }}>Eligible</th>
                <th style={{ textAlign: 'right' }}>Per member</th>
                <th style={{ textAlign: 'right' }}>Retained</th>
              </tr>
            </thead>
            <tbody>
              {d.pools.map((p) => (
                <tr key={p.category}>
                  <td className="cell-strong">{p.label}</td>
                  <td className="cell-num">{p.percent}%</td>
                  <td className="cell-num">{inr(p.poolPaise)}</td>
                  <td className="cell-num">{num(p.eligibleUserCount)}</td>
                  <td className="cell-num cell-strong">{inr(p.perUserPaise)}</td>
                  {/* The few paise that would not divide equally. Shown rather
                      than quietly handed to whoever sorted first. */}
                  <td className="cell-num cell-muted">{inr(p.remainderPaise, { paise: true })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="row row--wrap" style={{ gap: 'var(--s3)', marginTop: 12 }}>
        {d.status !== 'DISTRIBUTED' && (
          <Button size="sm" onClick={onCalculate}>
            {d.status === 'CALCULATED' ? 'Recalculate' : 'Enter profit & calculate'}
          </Button>
        )}
        {d.status === 'CALCULATED' && (
          <Button size="sm" variant="primary" loading={busy} onClick={onDistribute}>
            Distribute
          </Button>
        )}
        {d.status === 'DISTRIBUTED' &&
          (d.visibility === 'HIDDEN' ? (
            <Button size="sm" variant="primary" onClick={onReveal}>
              Reveal to members
            </Button>
          ) : (
            <Button size="sm" variant="ghost" loading={busy} onClick={onHide}>
              Hide again
            </Button>
          ))}
      </div>

      <div className="cell-muted" style={{ marginTop: 10, lineHeight: 1.7 }}>
        {d.calculatedAt && <div>Calculated {dateTime(d.calculatedAt)}</div>}
        {d.distributedAt && <div>Distributed {dateTime(d.distributedAt)}</div>}
        {d.revealedAt && <div>Revealed {dateTime(d.revealedAt)}</div>}
        {d.notes && <div>Note: {d.notes}</div>}
      </div>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="cell-muted">{label}</div>
      <div className="cell-strong" style={{ fontSize: 14.5 }}>
        {value}
      </div>
    </div>
  );
}

function CalculateDialog({ d, onClose, onDone }: { d: Distribution; onClose: () => void; onDone: () => void }) {
  const [rupees, setRupees] = useState(d.netProfitPaise ? String(d.netProfitPaise / 100) : '');
  const [notes, setNotes] = useState(d.notes ?? '');
  const toast = useToast();

  const run = useMutation({
    mutationFn: () =>
      api.post(`/admin/profit/${d.id}/calculate`, {
        netProfitPaise: Math.round(Number(rupees) * 100),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }),
    onSuccess: onDone,
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const value = Number(rupees);
  const ready = rupees.trim() !== '' && Number.isFinite(value) && value >= 0;

  return (
    <Modal
      title={`Net profit for ${d.financialYear}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={run.isPending} disabled={!ready} onClick={() => run.mutate()}>
            Calculate
          </Button>
        </>
      }
    >
      <div className="stack">
        <Notice tone="info" title="This figure is entered, not calculated">
          The app records money coming in, not what it costs to run — there is no expense or payroll data here, so it
          cannot work out a profit. Enter the audited figure. Turnover for this year was{' '}
          <strong>{inr(d.turnoverPaise)}</strong> excluding GST, which is what to check it against.
        </Notice>

        <Field label="Net profit (₹)">
          <Input type="number" min={0} step="0.01" value={rupees} onChange={(e) => setRupees(e.target.value)} />
        </Field>

        <Field label="Where this figure came from" hint="Recorded with the calculation. Optional but worth filling in.">
          <Input
            value={notes}
            placeholder="e.g. Audited accounts signed 14 Jun, filed with ROC"
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

function ConfigDialog({
  current,
  rules,
  onClose,
  onDone,
}: {
  current: Category[];
  rules: Rule[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [rows, setRows] = useState<Category[]>(current.length ? current : []);
  const toast = useToast();

  const save = useMutation({
    mutationFn: () => api.post('/admin/profit/config', { categories: rows }),
    onSuccess: onDone,
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const patch = (i: number, next: Partial<Category>) =>
    setRows(rows.map((r, j) => (i === j ? { ...r, ...next } : r)));

  const total = rows.reduce((s, r) => s + (Number(r.percent) || 0), 0);

  return (
    <Modal
      wide
      title="Profit categories"
      sub="Each category takes a share of net profit, divided equally among everyone the rule matches."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={total > 100} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        {rows.map((r, i) => (
          <div key={i} className="row row--wrap" style={{ gap: 'var(--s3)', alignItems: 'flex-end' }}>
            <Field label="Label">
              <Input value={r.label} onChange={(e) => patch(i, { label: e.target.value })} />
            </Field>
            <Field label="Share %">
              <Input
                type="number"
                min={0}
                max={100}
                value={String(r.percent)}
                onChange={(e) => patch(i, { percent: Number(e.target.value) })}
              />
            </Field>
            <Field label="Eligible members">
              <Select
                value={r.rule}
                options={rules.map((rule) => ({ value: rule.key, label: rule.label }))}
                onChange={(e) => patch(i, { rule: e.target.value })}
              />
            </Field>
            <Button size="sm" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
              Remove
            </Button>
          </div>
        ))}

        <div className="row" style={{ gap: 'var(--s3)' }}>
          <Button
            size="sm"
            onClick={() =>
              setRows([...rows, { key: `CAT${rows.length + 1}`, label: '', percent: 10, rule: rules[0]?.key ?? '' }])
            }
          >
            + Add category
          </Button>
          <span className={total > 100 ? 'cell-strong' : 'cell-muted'}>
            {total}% allocated{total > 100 && ' — more than the profit'}
          </span>
        </div>

        <Notice tone="info" title="These rules read live data">
          IUC, SUC and TUC as membership tiers (v2 §24–28) are not built and are blocked by §0.2(a), so each category is
          pointed at a rule over accounts that actually exist. When the tiers ship, repoint the categories — nothing
          else changes.
        </Notice>
      </div>
    </Modal>
  );
}
